/* eslint-disable react-hooks/exhaustive-deps */
import {KlassappTableHOC} from "HOC"
import styles from "./DepartmentForm.module.css"
import {ConfirmPopup, KlassappTable, KlassappTableHeader} from "uiKit"
import {useEffect, useState} from "react"
import {checkPermission, handleError, toastError} from "helpers"
import {Order} from "types/common"
import {getPermissionsByDepartment} from "../helper"
import {BaseDepartmentId} from "types/departments"
import {isEmpty} from "lodash"
import {departmentsService} from "services"
import {useModel, useVisible} from "hooks"
import {Checkbox} from "antd"
import {LinkDepartmentForm} from "./parts"
import {AddItemCircleButton, Icon} from "components"

function DepartmentForm(props) {
    const {
        data,
        page,
        total,
        pageSize,
        columns,
        fields,
        allFields,
        isLoading,
        orderField,
        dispatch,
        isLoadedTableFuncs,
        menuActions,
        departmentId
    } = props

    const [currentItem, setCurrentItem] = useState<any>()
    const formVisible = useVisible()
    const model = useModel()
    const [formKey, setFormKey] = useState(Date.now())
    const deleteConfirmPopup = useVisible()
    const [selectedItem, setSelectedItem] = useState<any>()

    useEffect(() => {
        dispatch({isClassComponent: false})
        props.dispatchFunc([
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getMenuActions", func: getMenuActions},
            {key: "getColumns", func: getColumns}
        ])
        dispatch({orderField: {field: "id", order: Order.Asc}})
    }, [])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getData()
        }
    }, [isLoadedTableFuncs, page, pageSize, orderField?.field, orderField?.order])

    const canShowActionIcon = () => {
        return checkPermission(getPermissionsByDepartment(BaseDepartmentId.Admissions), model)
    }

    const onClickEdit = (editItem) => {
        if (!editItem.allowTaskLinked) {
            toastError("You are not allowed to edit the system form.")
            return
        }
        onClickRowItem(editItem)
    }

    const handleDelete = async () => {
        try {
            await departmentsService.deleteDepartmentForm({ids: [selectedItem.id]})
            await getData()
        } catch (err) {
            handleError(err)
        } finally {
            setSelectedItem(undefined)
            deleteConfirmPopup.close()
        }
    }

    const onClickDelete = (deletedItem) => {
        if (!deletedItem.allowTaskLinked) {
            toastError("You are not allowed to delete the system form.")
            return
        }
        setSelectedItem(deletedItem)
        deleteConfirmPopup.open()
    }

    function getMenuActions() {
        return [
            {
                title: "Edit",
                icon: "EDIT",
                action: onClickEdit,
                canShow: canShowActionIcon
            },
            {
                title: "Remove",
                icon: "DELETE",
                action: onClickDelete,
                canShow: canShowActionIcon
            }
        ]
    }

    const onClickRowItem = (row) => {
        setCurrentItem({...row})
        formVisible.open()
    }

    const getParams = () => {
        const params: any = {
            departmentId,
            range: {
                page,
                pageSize
            },
            sort: {
                orderBy: "id",
                orderDir: Order.Asc
            }
        }
        if (!isEmpty(orderField)) {
            params.sort = {
                orderBy: orderField.field,
                orderDir: orderField.order
            }
        }
        return params
    }

    async function getData() {
        try {
            dispatch({isLoading: true})

            const params = getParams()
            const {data, total} = await departmentsService.getAllDepartmentForms(params)

            const newData = data.map((item) => {
                item.formIdHtml = renderFormIdHtml(item)
                item.tasksHtml = renderTasksHtml(item)
                return item
            })

            dispatch({data: newData, total})
        } catch (error) {
            handleError(error)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const renderFormIdHtml = (item) => {
        if (!item.allowTaskLinked)
            return (
                <div className={styles.formIdHtml}>
                    <span>{item.formId}</span>
                    <Icon icon="LOCK_CHECKMARK" />
                </div>
            )
        return <span>{item.formId}</span>
    }

    const renderTasksHtml = (item) => {
        return (
            <>
                {item.tasks?.map((task) => {
                    return (
                        <div className={styles.taskItem} key={task.id}>
                            <span className={styles.taskCode}>{task.code}</span>
                            <span className={styles.taskName}>{task.name}</span>
                        </div>
                    )
                })}
            </>
        )
    }

    function getColumns() {
        return [
            {
                title: "Form ID",
                field: "formIdHtml",
                style: {minWidth: "80px"},
                sortable: true,
                orderField: "formId"
            },
            {
                title: "Display Name",
                field: "displayName",
                style: {minWidth: "100px"},
                sortable: true,
                orderField: "displayName"
            },
            {
                title: "URL",
                field: "url",
                style: {minWidth: "100px"},
                sortable: true,
                orderField: "url"
            },
            {
                title: "Tasks",
                field: "tasksHtml",
                style: {minWidth: "100px"},
                sortable: false
            }
        ]
    }

    function getFields() {
        return ["Form ID", "Display Name", "URL", "Allow Task Linked", "Tasks"]
    }

    const handleSubmitSuccessfully = () => {
        setCurrentItem(undefined)
        formVisible.close()
        getData()
        setFormKey(Date.now())
    }

    const handleCreate = () => {
        setCurrentItem(undefined)
        formVisible.open()
    }

    return (
        <div className={styles.wrapper}>
            <KlassappTableHeader
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
            />
            <KlassappTable
                columns={columns}
                data={data}
                menuActions={menuActions}
                isLoading={isLoading}
                fields={fields}
                allFields={allFields}
                orderField={orderField}
                onClickRowItem={props.onClickRowItem}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={props.onUpdateRowData}
                onUpdateTableData={props.onUpdateTableData}
                onClickSortColumn={props.onClickSortColumn}
            />
            {canShowActionIcon && !isLoading && <AddItemCircleButton onClick={handleCreate} />}
            <LinkDepartmentForm
                open={formVisible.isVisible}
                onClose={() => formVisible.close()}
                item={currentItem}
                onSuccess={handleSubmitSuccessfully}
                departmentId={departmentId}
                key={formKey}
            />
            <ConfirmPopup
                isVisible={deleteConfirmPopup.isVisible}
                title="Confirm"
                onClose={deleteConfirmPopup.close}
                onConfirm={handleDelete}
                loading={isLoading}
                description="Are you sure to delete this form?"
                icon="DELETE"
            />
        </div>
    )
}

export const DepartmentFormSettings = KlassappTableHOC(DepartmentForm)
